import { useNavigate } from 'react-router-dom';
import { DollarSign, Activity, Shield, Rocket } from 'lucide-react';

export function LandingPage() {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0F0F0F 50%, #0a0a0a 100%)',
        position: 'relative'
      }}
    >
      {/* Subtle noise texture overlay */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Header with Logo */}
      <header className="relative z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            {/* Logo */}
            <img 
              src="/Logo.png" 
              alt="MarketTag Logo" 
              className="w-10 h-10 object-contain flex-shrink-0"
            />
            {/* MarketTag text */}
            <span 
              className="text-2xl font-semibold"
              style={{ 
                fontFamily: 'Montserrat, sans-serif',
                color: '#F9F9F9'
              }}
            >
              MarketTag
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto text-center">
            {/* Headline */}
            <h1 
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6"
              style={{ color: '#F9F9F9' }}
            >
              Find your next winning stock in minutes, not hours.
            </h1>

            {/* Subheadline */}
            <p 
              className="text-lg sm:text-xl md:text-2xl mb-8"
              style={{ color: '#9E9E9E' }}
            >
              We turn complex financial data into four simple scores and tags. Screen 100 stocks in minutes using smart badges that actually make sense.
            </p>

            {/* CTA Button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-all transform hover:scale-105 mb-12"
              style={{ 
                backgroundColor: '#3ECF8E', 
                color: '#0F0F0F',
                boxShadow: '0 8px 32px rgba(62, 207, 142, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4DD99D';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(62, 207, 142, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3ECF8E';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(62, 207, 142, 0.3)';
              }}
            >
              Try our Beta
            </button>

            {/* Four Score Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto mt-8">
              {/* Value Card */}
              <div 
                className="rounded-xl p-6 flex flex-col items-center gap-3 transition-transform hover:scale-105"
                style={{ 
                  backgroundColor: '#1E1E1E',
                  border: '2px solid #3ECF8E',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <DollarSign className="w-10 h-10" style={{ color: '#3ECF8E' }} />
                </div>
                <span className="text-white font-bold text-lg">Value</span>
              </div>

              {/* Growth Card */}
              <div 
                className="rounded-xl p-6 flex flex-col items-center gap-3 transition-transform hover:scale-105"
                style={{ 
                  backgroundColor: '#1E1E1E',
                  border: '2px solid #3ECF8E',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <Activity className="w-10 h-10" style={{ color: '#3ECF8E' }} />
                </div>
                <span className="text-white font-bold text-lg">Growth</span>
              </div>

              {/* Health Card */}
              <div 
                className="rounded-xl p-6 flex flex-col items-center gap-3 transition-transform hover:scale-105"
                style={{ 
                  backgroundColor: '#1E1E1E',
                  border: '2px solid #3ECF8E',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <Shield className="w-10 h-10" style={{ color: '#3ECF8E' }} />
                </div>
                <span className="text-white font-bold text-lg">Health</span>
              </div>

              {/* Momentum Card */}
              <div 
                className="rounded-xl p-6 flex flex-col items-center gap-3 transition-transform hover:scale-105"
                style={{ 
                  backgroundColor: '#1E1E1E',
                  border: '2px solid #3ECF8E',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="w-16 h-16 flex items-center justify-center">
                  <Rocket className="w-10 h-10" style={{ color: '#3ECF8E' }} />
                </div>
                <span className="text-white font-bold text-lg">Momentum</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

